<template>
  <v-container fluid class="general-info-page">
    <!-- Header -->
    <div class="gi-header">
      <div class="gi-header__top">
        <div class="gi-header__info">
          <h1 class="gi-header__title">{{ model.add.name }}</h1>
        </div>
      </div>
    </div>

    <!-- Alerts -->
    <AAlertBanner :messages="alertError" hide-title />

    <AAlertBanner variant="danger" :messages="apiSubmit.error" hide-title icon="mdi-alert" />

    <!-- Confirmation Dialog -->
    <v-dialog v-model="generalInfoDialog" max-width="460" content-class="gi-confirm-dialog">
      <div class="gi-confirm-card">
        <h3 class="gi-confirm-card__title">{{ modalTitle }}</h3>
        <p class="gi-confirm-card__message">{{ modalText }}</p>
        <div class="gi-confirm-card__actions">
          <button
            class="gi-confirm-card__btn gi-confirm-card__btn--cancel"
            @click="generalInfoDialog = false"
          >
            Cancel
          </button>
          <button
            class="gi-confirm-card__btn gi-confirm-card__btn--confirm"
            @click="modalGeneralInfo()"
          >
            Submit
          </button>
        </div>
      </div>
    </v-dialog>

    <!--BOC:[contents]-->
    <div class="section-card">
      <!-- Tabs -->
      <div class="gi-tabs">
        <button
          class="gi-tab"
          :class="{ 'gi-tab--active': tab === 'tab-1' }"
          @click="tab = 'tab-1'"
        >
          <v-icon size="16" :color="tab === 'tab-1' ? '#0ea5e9' : '#94a3b8'" class="mr-2">mdi-text-box-edit</v-icon>
          General Remarks
        </button>
        <button
          class="gi-tab"
          :class="{ 'gi-tab--active': tab === 'tab-2' }"
          @click="tab = 'tab-2'"
        >
          <v-icon size="16" :color="tab === 'tab-2' ? '#0ea5e9' : '#94a3b8'" class="mr-2">mdi-cloud-upload-outline</v-icon>
          Upload Attachment
        </button>
      </div>

      <!-- Tab 1: General Remarks -->
      <div v-show="tab === 'tab-1'" class="section-body">
        <div class="form-row">
          <label class="form-label">Company</label>
          <div class="form-field">
            <v-text-field
              dense
              filled
              v-model="companyName"
              disabled
            ></v-text-field>
          </div>
        </div>

        <div class="form-row">
          <label class="form-label">General Remarks</label>
          <div class="form-field">
            <v-textarea
              filled
              auto-grow
              v-model="formData.generalRemarks"
            ></v-textarea>
          </div>
        </div>

        <div class="form-actions">
          <button
            class="gi-submit-btn"
            @click="submitGeneralRemarks()"
            :disabled="apiSubmit.isLoading"
          >
            Submit
          </button>
        </div>
      </div>

      <!-- Tab 2: Upload Attachment -->
      <div v-show="tab === 'tab-2'" class="section-body">
        <div class="form-row">
          <label class="form-label">Attachment Name</label>
          <div class="form-field">
            <v-text-field
              dense
              filled
              v-model.trim="formData.attachmentName"
              :error-messages="errorField.attachmentName"
            >
              <template v-slot:append>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on"> mdi-information </v-icon>
                  </template>
                  Etc: Authorization Letter, Credit Note, ...
                </v-tooltip>
              </template>
            </v-text-field>
          </div>
        </div>

        <div class="form-row">
          <label class="form-label">Attachment Type</label>
          <div class="form-field">
            <div class="gi-select" v-click-outside="() => attachmentTypeDropdownOpen = false">
              <div
                class="gi-select__trigger"
                :class="{ 'gi-select__trigger--open': attachmentTypeDropdownOpen, 'gi-select__trigger--error': errorField.attachmentType }"
                @click="attachmentTypeDropdownOpen = !attachmentTypeDropdownOpen"
              >
                <div class="gi-select__value">
                  <span v-if="formData.attachmentType !== null && formData.attachmentType !== undefined">
                    {{ getAttachmentTypeText(formData.attachmentType) }}
                  </span>
                  <span v-else class="gi-select__placeholder">Select attachment type...</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="gi-select__arrow" :class="{ 'gi-select__arrow--open': attachmentTypeDropdownOpen }">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div v-if="attachmentTypeDropdownOpen" class="gi-select__dropdown">
                <div
                  v-for="item in attachmentTypeItems"
                  :key="item.value"
                  class="gi-select__option"
                  :class="{ 'gi-select__option--selected': formData.attachmentType === item.value }"
                  @click="selectAttachmentType(item.value)"
                >
                  {{ item.text }}
                </div>
              </div>
              <div v-if="errorField.attachmentType" class="gi-select__error">
                {{ Array.isArray(errorField.attachmentType) ? errorField.attachmentType.join(', ') : errorField.attachmentType }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-row">
          <label class="form-label">Attachment Inv No.</label>
          <div class="form-field">
            <v-text-field
              dense
              filled
              placeholder="etc: YGC-XXXXXX, CNYGC-XXXXXX (optional)"
              v-model.trim="formData.attachmentInvNo"
              :error-messages="errorField.attachmentInvNo"
            >
              <template v-slot:append>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on"> mdi-information </v-icon>
                  </template>
                  Only upload if any (for tracing purpose)
                </v-tooltip>
              </template>
            </v-text-field>
          </div>
        </div>

        <div class="form-row">
          <label class="form-label">Attachment File</label>
          <div class="form-field">
            <v-file-input
              :prepend-icon="null"
              prepend-inner-icon="mdi-paperclip"
              dense
              filled
              v-model="formData.attachmentFile"
              :error-messages="errorField.attachmentFile"
            >
            </v-file-input>
          </div>
        </div>

        <div class="form-actions">
          <button
            class="gi-submit-btn"
            @click="submitGeneralAttachment()"
            :disabled="apiSubmit.isLoading"
          >
            Submit
          </button>
        </div>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import HrdcData from "@/services/hrdc_event/objects/globalData";
import { mapState } from "vuex";
import hrdcFunction from "@/objects/hrdcFunction";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    apiGetApplicationSingleDetail: new Api(),
    apiSubmit: new Api(),
    apiSubmitGeneralAttachment: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    formData: {
      applicationNo: null,
      generalRemarks: null,
      attachmentName: null,
      attachmentFile: null,
      attachmentType: null,
      attachmentInvNo: null,
    },
    errorField: {
      applicationNo: null,
      generalRemarks: null,
      attachmentName: null,
      attachmentFile: null,
      attachmentType: null,
    },
    loadingDialog: false,
    generalInfoDialog: false,
    editMode: false,
    btnDisabled: false,
    companyName: null,
    applicationPrice: null,
    tab: "tab-1",
    attachmentTypeDropdownOpen: false,
    modalTitle: null,
    modalText: null,
    modalMethod: "GENERAL_REMARKS",
    attachmentTypeItems: [],
  }),
  async created() {
    this.showLoadingDialog();
    this.formData.applicationNo = this.$route.query._ausk;
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/general_info/create-general-remarks`,
        method: "post",
      },
      apiGetApplicationSingleDetail: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-single-application`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiSubmitGeneralAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/general_info/create-general-attachment`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    this.rolesAssignation(this.auth.roles);

    this.apiGetApplicationSingleDetail.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          this.formData.generalRemarks = data.generalRemarks;
          this.companyName = response.company.oldCompanyName;
          this.applicationPrice = data.totalPrice;
        }

        this.hideLoadingDialog();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationSingleDetail.fetch();

    // Assign attachment type stages options.
    this.attachmentTypeItems = [
      {
        text: "HRDC Invoice",
        value: this.hrdcData.stagePriority.manualInvoiceToHrdc,
      },
      {
        text: "Client Invoice",
        value: this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice,
      },
      {
        text: "Credit Note",
        value: this.hrdcData.stagePriority.creditNoteGenerated,
      },
      {
        text: "Others",
        value: this.hrdcData.stagePriority.otherAttachment,
      },
    ];
  },
  methods: {
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    modalGeneralInfo() {
      this.generalInfoDialog = !this.generalInfoDialog;

      switch (this.modalMethod) {
        case "GENERAL_REMARKS":
          this.submitGeneralRemarksApi();
          break;

        case "GENERAL_ATTACHMENT":
          this.submitGeneralAttachmentApi();
          break;
      }
    },
    getAttachmentTypeText(val) {
      const item = this.attachmentTypeItems.find(function(i) { return i.value === val; });
      return item ? item.text : '';
    },
    selectAttachmentType(val) {
      this.formData.attachmentType = val;
      this.attachmentTypeDropdownOpen = false;
    },
    redirectBack() {
      let route = this.eventRoute("Application");
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state[this.service.key].data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    submitGeneralRemarks() {
      this.generalInfoDialog = true;

      this.modalTitle = "Submit Remarks";
      this.modalText = "Are you sure to update the remarks ?";
      this.modalMethod = "GENERAL_REMARKS";
    },
    submitGeneralAttachment() {
      this.generalInfoDialog = true;

      this.modalTitle = "Submit General Attachment";
      this.modalText = "Are you sure to submit this attachment ?";
      this.modalMethod = "GENERAL_ATTACHMENT";
    },
    async submitGeneralRemarksApi() {
      this.showLoadingDialog();

      const fd = new FormData();
      fd.append("applicationUuid", this.formData.applicationNo);
      fd.append("referById", this.auth.uuid);
      fd.append("generalRemarks", this.formData.generalRemarks);

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message: response.message,
              messageType: "error",
              timeout: 2000,
            });
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });
            this.$router.push({
              name: this.eventRoute("ViewApplication"),
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
                _ausk: this.$route.query._ausk,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
      this.hideLoadingDialog();
    },
    async submitGeneralAttachmentApi() {
      this.showLoadingDialog();

      // Set url & params.
      this.apiSubmit.setUrl(
        `${
          this.$service[this.service.key]
        }/v1/en/console/general_info/create-general-attachment`
      );

      const fd = new FormData();
      fd.append("applicationUuid", this.formData.applicationNo);
      fd.append("referById", this.auth.uuid);
      fd.append("attachmentName", this.formData.attachmentName);
      fd.append("attachmentFile", this.formData.attachmentFile);
      fd.append("attachmentType", this.formData.attachmentType);
      fd.append("attachmentInvNo", this.formData.attachmentInvNo);

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message: response.message,
              messageType: "error",
              timeout: 2000,
            });
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });
            this.$router.push({
              name: this.eventRoute("Application"),
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
                _ausk: this.$route.query._ausk,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
      this.hideLoadingDialog();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .general-info-page {
  background-color: var(--app-page);
}

.dark-mode .gi-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .gi-confirm-card {
  background: var(--app-form-tile);
}

.dark-mode .gi-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .gi-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .gi-confirm-card__btn--cancel {
  background: var(--app-form-tile);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .gi-confirm-card__btn--cancel:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .gi-tabs {
  background: var(--app-form-tile);
}

.dark-mode .gi-tab {
  color: var(--app-text-muted);
}

.dark-mode .gi-tab:hover {
  color: var(--app-text);
  background: var(--app-form-tile-hover);
}

.dark-mode .gi-tab--active {
  background: var(--app-accent-soft);
}

.dark-mode .gi-select__trigger {
  border: 1px solid var(--app-field-border);
  background: var(--app-field);
  color: var(--app-text);
}

.dark-mode .gi-select__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .gi-select__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .gi-select__arrow {
  color: var(--app-text-faint);
}

.dark-mode .gi-select__dropdown {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .gi-select__dropdown::-webkit-scrollbar-thumb {
  background: var(--app-form-tile);
}

.dark-mode .gi-select__option {
  color: var(--app-text);
}

.dark-mode .gi-select__option:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .gi-select__option--selected {
  background: var(--app-accent-soft);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-field) !important;
  border: 1.5px solid var(--app-field-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-field-disabled) !important;
  border: 1px dashed var(--app-field-disabled-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-text-muted);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.general-info-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  zoom: 1.1;
}

/* ============================================
   HEADER
   ============================================ */
.gi-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.gi-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.gi-header__info {
  flex: 1;
  min-width: 0;
}

.gi-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

/* ============================================
   CONFIRMATION DIALOG
   ============================================ */
.gi-confirm-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.gi-confirm-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 32px 24px;
  text-align: center;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.gi-confirm-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.gi-confirm-card__message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 24px;
  line-height: 1.5;
}

.gi-confirm-card__actions {
  display: flex;
  gap: 12px;
}

.gi-confirm-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.gi-confirm-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.gi-confirm-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.gi-confirm-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.gi-confirm-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   SECTION CARD
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-body {
  padding: 22px;
  background: #fff;
}

/* ============================================
   TABS
   ============================================ */
.gi-tabs {
  display: flex;
  gap: 4px;
  padding: 14px 28px 0;
  background: #eaecef;
}

.gi-tab {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  color: #64748b;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 8px 8px 0 0;
}

.gi-tab:hover {
  color: #334155;
  background: #f8fafc;
}

.gi-tab--active {
  color: #0ea5e9;
  border-bottom-color: #0ea5e9;
  background: #f0f9ff;
}

/* ============================================
   FORM ROWS & FIELDS
   ============================================ */
.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.form-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 10px;
}

.form-field {
  flex: 1;
  min-width: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

/* Submit Button */
.gi-submit-btn {
  padding: 10px 32px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  background: #56bde8;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}

.gi-submit-btn:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

.gi-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============================================
   CUSTOM SELECT DROPDOWN
   ============================================ */
.gi-select {
  position: relative;
}

.gi-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  color: #1e293b;
}

.gi-select__trigger:hover {
  border-color: #94a3b8;
}

.gi-select__trigger--open {
  border-color: #56bde8;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12);
}

.gi-select__trigger--error {
  border-color: #ef4444;
}

.gi-select__value {
  flex: 1;
  min-width: 0;
}

.gi-select__placeholder {
  color: #94a3b8;
}

.gi-select__arrow {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
  margin-left: 8px;
  transition: transform 0.2s ease;
}

.gi-select__arrow--open {
  transform: rotate(180deg);
}

.gi-select__dropdown {
  margin-top: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.gi-select__dropdown::-webkit-scrollbar {
  width: 5px;
}

.gi-select__dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.gi-select__dropdown::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.gi-select__option {
  padding: 9px 12px;
  border-radius: 7px;
  font-size: 0.85rem;
  color: #334155;
  cursor: pointer;
  transition: background 0.12s;
}

.gi-select__option:hover {
  background: #fff;
}

.gi-select__option--selected {
  background: #e0f2fe;
  color: #0369a1;
  font-weight: 600;
}

.gi-select__option--selected:hover {
  background: #bae6fd;
}

.gi-select__error {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #ef4444;
}

/* Dark mode */
.dark-mode .gi-select__trigger {
  background: var(--app-field);
  border-color: var(--app-field-border);
  color: var(--app-text);
}

.dark-mode .gi-select__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .gi-select__trigger--open {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .gi-select__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .gi-select__dropdown {
  background: var(--app-form-tile);
  border-color: var(--app-border);
}

.dark-mode .gi-select__option {
  color: var(--app-neutral-fg);
}

.dark-mode .gi-select__option:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .gi-select__option--selected {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}

/* ============================================
   VUETIFY INPUT OVERRIDES
   ============================================ */
.form-field >>> .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.form-field >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 40px !important;
}

.form-field >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
  min-height: 0 !important;
}

.form-field >>> .v-messages {
  min-height: 0 !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
}

.form-field >>> .v-input--is-focused .v-input__slot {
  border-color: #56bde8 !important;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12) !important;
}

.form-field >>> .v-input__slot::before,
.form-field >>> .v-input__slot::after {
  display: none !important;
}

/* Editable fields - white background with visible border */
.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: #fff !important;
  border: 1.5px solid #d1d5db !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: #94a3b8 !important;
}

/* Disabled / read-only fields - flat muted look */
.form-field >>> .v-input--is-disabled .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px dashed #e2e8f0 !important;
  cursor: not-allowed !important;
}

.form-field >>> .v-input--is-disabled input,
.form-field >>> .v-input--is-disabled textarea,
.form-field >>> .v-input--is-disabled .v-select__selection {
  color: #94a3b8 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-focused) > .v-input__control > .v-input__slot::before {
  border: none !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot::after {
  border: none !important;
}

.form-field >>> .v-label {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  color: #64748b;
}

.form-field >>> .v-text-field input,
.form-field >>> .v-select__selection,
.form-field >>> .v-text-field textarea {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1e24;
}

/* Error state */
.form-field >>> .v-input--has-state .v-input__slot {
  border-color: #f87171 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled).v-input--has-state > .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
}

.form-field >>> .v-messages__message {
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  color: #ef4444;
}

/* ============================================
   FADE TRANSITION
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .general-info-page {
  background-color: var(--app-page);
}

.dark-mode .gi-header {
  background: var(--app-elevated);
}

.dark-mode .gi-header__title {
  color: var(--app-text);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .gi-tabs {
  background: var(--app-form-tile);
}

.dark-mode .gi-tab {
  color: var(--app-text-muted);
}

.dark-mode .gi-tab--active {
  color: var(--app-accent);
  border-bottom-color: var(--app-accent);
  background: var(--app-accent-soft);
}

.dark-mode .form-label {
  color: var(--app-neutral-fg);
}

.dark-mode .gi-confirm-card {
  background: var(--app-form-tile);
}

.dark-mode .gi-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .gi-confirm-card__message {
  color: var(--app-text-muted);
}

/* ============================================
   MOBILE RESPONSIVE
   ============================================ */
@media (max-width: 599px) {
  .general-info-page {
    padding: 12px 8px !important;
    zoom: 1;
  }

  .gi-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .gi-header__top {
    flex-wrap: wrap;
    padding: 16px 16px 12px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 12px;
  }

  .gi-header__title {
    font-size: 1.05rem;
    white-space: normal;
  }

  .gi-tabs {
    padding: 0 12px;
    gap: 0;
  }

  .gi-tab {
    font-size: 0.78rem;
    padding: 10px 12px;
  }

  .section-card {
    border-radius: 14px;
  }

  .section-body {
    padding: 14px;
    overflow: hidden;
    word-break: break-word;
  }

  .form-row {
    flex-direction: column;
    gap: 2px;
    align-items: stretch;
  }

  .form-label {
    flex: none;
    width: 100%;
    font-size: 0.72rem;
    padding-top: 0;
    margin-bottom: 2px;
  }

  .form-field {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .form-field >>> .v-input {
    width: 100% !important;
  }

  .form-field >>> .v-input__control {
    width: 100% !important;
  }

  .form-field >>> .v-input__slot {
    width: 100% !important;
    box-sizing: border-box !important;
  }
}


/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text) !important;
}

</style>
